import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchMyReviews,
  fetchMyReviewsTotals,
  fetchSavedFiltersByView,
  fetchSubmissionsDashBoardConfig,
  IMyReviewsFilters,
  ISubmissionSortOption
} from 'src/api/queries';
import { useKeycloak } from 'src/components/AuthHOC';
import { TABLE_PAGE_LIMIT } from 'src/constants';

export const useReviews = (
  reviewListType: 'active' | 'completed',
  page: number,
  offset: number,
  filters: IMyReviewsFilters,
  sort?: ISubmissionSortOption,
  limit: number = TABLE_PAGE_LIMIT
) => {
  const { currentUser } = useKeycloak();

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      retry: 0,
      queryKey: ['review-list', currentUser?.id, filters, sort, page, limit],
      queryFn: () =>
        fetchMyReviews(reviewListType, offset, limit, filters, sort),

      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage) return undefined;

        const maxOffset = lastPage.total;
        const nextOffset = allPages.length * limit;

        return nextOffset < maxOffset ? nextOffset : undefined;
      }
    });

  const totalSubmissions = data?.pages?.[data.pages.length - 1]?.total ?? 0;

  return {
    data,
    totalSubmissions,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  };
};

export const useReviewsTotals = () => {
  return useQuery({
    queryKey: ['review-list-totals'],
    queryFn: fetchMyReviewsTotals
  });
};

export const useReviewDashBoardConfig = (view: string, workspaceId?: string) =>
  useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ view, workspaceId })
  });

export const defaultValues: IMyReviewsFilters = {
  journalId: [],
  searchParams: ''
};

export const useReviewerSavedFilters = (view: string) => {
  return useQuery({
    queryKey: ['submission-filter', { view }],
    queryFn: () => (view ? fetchSavedFiltersByView(view) : null)
  });
};
